/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import {
  useNavigation,
  useRoute,
  type NavigationProp,
  type ParamListBase,
} from '@react-navigation/native';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketButton } from '../../shared/ticket/TicketButton'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { OrganizationIdentitySection } from './components/OrganizationIdentitySection';
import { OrganizationLogoSection } from './components/OrganizationLogoSection';
import { useOrganizationScreen } from './useOrganizationScreen';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { isBillingEnabled } from '../../shell/billing/isBillingEnabled';
import { SettingsNavListCard } from './components/settingsScreenLayout';
import { SettingsNavigationRow } from '../components/SettingsNavigationRow';
import { OrganizationSwitcherSheet } from '../../shell/session/components/OrganizationSwitcherSheet';
import { useOrganizationSwitcherSheet } from '../../shell/session/hooks/useOrganizationSwitcherSheet';
import { ScrollView, RefreshControl } from 'react-native';
import type { OrganizationIdentityRouteParams } from '../navigation/openOrganizationIdentity';

export const OrganizationScreen = () => {
  const vm = useOrganizationScreen();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const route = useRoute();
  const routeParams = (route.params ?? {}) as OrganizationIdentityRouteParams;
  const orgSwitcher = useOrganizationSwitcherSheet(navigation);
  const { layoutWide } = useTabWorkspaceLayout();
  const scrollRef = useRef<ScrollView>(null);
  const logoSectionOffsetY = useRef(0);

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  useEffect(() => {
    if (routeParams.focusSection !== 'logo') return;
    if (logoSectionOffsetY.current <= 0) return;
    const y = Math.max(0, logoSectionOffsetY.current - spacing.md);
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y, animated: true });
    });
  }, [routeParams.focusSection, vm.organizationLogoUrl, vm.entLoading]);

  const kpiItems = useMemo<TicketKpiItem[]>(() => {
    return [
      {
        label: 'Sucursales',
        value: String(vm.branchCount),
        tone: 'default',
        testID: 'organization.kpi.branches',
      },
      {
        label: 'Equipo',
        value: vm.memberCount == null ? '…' : String(vm.memberCount),
        tone: 'default',
        testID: 'organization.kpi.team',
      },
      {
        label: 'Módulos activos',
        value: `${vm.activeModulesCount}/${vm.totalModulesCount}`,
        tone: 'default',
        testID: 'organization.kpi.modules',
      },
    ];
  }, [vm.branchCount, vm.memberCount, vm.activeModulesCount, vm.totalModulesCount]);

  const { refreshing, onRefresh } = useScreenLoadShell(
    'Organization',
    async (opts) => { await vm.refreshEntitlements(opts); }
  );

  const loading =
    vm.entLoading && !vm.entitlements ? (
      <View style={styles.loadingRow}>
        <ActivityIndicator size="small" color={ticketColors.gold} />
        <Text style={styles.loadingText}>Cargando…</Text>
      </View>
    ) : null;

  return (
    <ShellContentFrame style={ticketSurfaces.screen}>
      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => void onRefresh()}
            tintColor={ticketColors.gold}
            colors={[ticketColors.gold]}
          />
        }
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.stack} testID="organization.screen">
          {/* Header Square */}
          <TicketModuleHeader
            kicker="Organización"
            title={vm.organizationDisplayName}
            meta={vm.businessTypeName ?? 'Sin rubro'}
            testID="organization.header"
          />

          {/* KPIs */}
          <PaperSheet testID="organization.kpi.sheet">
            <TicketKpiStrip
              items={kpiItems}
              layout={layoutWide ? 'desk' : 'mobile'}
              variant="soft"
            />
          </PaperSheet>

          {/* Identidad: editar nombre + cambiar rubro */}
          <View
            onLayout={(event) => {
              logoSectionOffsetY.current = event.nativeEvent.layout.y;
              if (routeParams.focusSection !== 'logo') return;
              const y = Math.max(0, logoSectionOffsetY.current - spacing.md);
              requestAnimationFrame(() => {
                scrollRef.current?.scrollTo({ y, animated: true });
              });
            }}
          >
            <PaperSheet padded testID="organization.identity.sheet">
            <View style={styles.identityStack}>
              <OrganizationLogoSection
                logoUrl={vm.organizationLogoUrl}
                organizationName={vm.organizationDisplayName}
                canEdit={vm.canEditOrg}
                uploading={vm.logoUploading}
                onChangeLogo={() => void vm.handleUploadOrganizationLogo()}
                onRemoveLogo={() => void vm.handleRemoveOrganizationLogo()}
              />
              <View style={styles.nameRow}>
                <View style={styles.nameCopy}>
                  <Text style={styles.nameLabel}>Nombre comercial</Text>
                  <Text style={styles.nameValue}>{vm.organizationDisplayName}</Text>
                </View>
                {vm.canEditOrg ? (
                  <TicketButton
                    variant="ghost"
                    label="Editar"
                    onPress={vm.openOrganizationEdit}
                    testID="organization.name.edit"
                  />
                ) : null}
              </View>

              <View style={styles.rubroRow}>
                <View style={styles.rubroCopy}>
                  <Text style={styles.rubroLabel}>Rubro</Text>
                  <Text style={styles.rubroValue}>{vm.businessTypeName ?? 'Sin rubro'}</Text>
                </View>
                {vm.canChangeBusinessType ? (
                  <TicketButton
                    variant="ghost"
                    label="Cambiar rubro"
                    onPress={vm.openChangeBusinessType}
                    testID="organization.rubro.change"
                  />
                ) : null}
              </View>
            </View>
          </PaperSheet>
          </View>

          {/* Tus negocios / Cambiar negocio */}
          {orgSwitcher.canOpenOrganizationMenu ? (
            <SettingsNavListCard>
              <SettingsNavigationRow
                title={orgSwitcher.organizationCount > 1 ? 'Tus negocios' : 'Cambiar o agregar negocio'}
                description={
                  orgSwitcher.organizationCount > 1
                    ? 'Elegí otro negocio de tu cuenta.'
                    : 'Abrí otro local o sumá un negocio más.'
                }
                onPress={orgSwitcher.open}
                iconName="layers-outline"
                showSeparator={false}
              />
            </SettingsNavListCard>
          ) : null}

          {loading}

          <OrganizationIdentitySection
            organizationNameFromApi={vm.organizationNameFromApi}
            entitlementsLoadMissing={vm.entitlementsLoadMissing}
            isOwner={vm.isOwner || vm.canEditOrg}
            organizationEditOpen={vm.organizationEditOpen}
            organizationNameInput={vm.organizationNameInput}
            onOrganizationNameChange={vm.setOrganizationNameInput}
            onCloseOrganizationEdit={vm.closeOrganizationEdit}
            onSaveOrganizationName={() => void vm.handleSaveOrganizationName()}
            savingOrganizationName={vm.savingOrganizationName}
            entitlementsRetrying={vm.entitlementsRetrying}
            onRetryEntitlements={vm.handleEntitlementsRetry}
            branchUsage={vm.branchUsage}
            planName={vm.planName}
            planCodeGratis={vm.planCodeGratis}
            showPlanKpi={vm.showPlanKpi}
            userUsage={vm.userUsage}
            onOpenBilling={vm.isOwner && isBillingEnabled() ? vm.openBilling : undefined}
            onOpenBusinessFunctions={vm.openBusinessFunctions}
            layoutWide={layoutWide}
          />

          {orgSwitcher.canOpenOrganizationMenu ? (
            <OrganizationSwitcherSheet
              visible={orgSwitcher.visible}
              onClose={orgSwitcher.close}
              navigation={orgSwitcher.navigation}
            />
          ) : null}
        </View>
      </ScrollView>
    </ShellContentFrame>
  );
};

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xxl,
  },
  stack: {
    gap: 10,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
  },
  loadingText: {
    fontSize: 14,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  identityStack: {
    gap: spacing.md,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  nameCopy: {
    flex: 1,
    gap: 2,
  },
  nameLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  nameValue: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  rubroRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  rubroCopy: {
    flex: 1,
    gap: 2,
  },
  rubroLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  rubroValue: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
});
